import { useRef, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Trash2, Download, Eraser, Pencil, Minus, Square, Circle, Undo2, PaintBucket } from "lucide-react";

const COLORS = ["#141414", "#E11D48", "#F59E0B", "#EAB308", "#16A34A", "#2563EB", "#7C3AED", "#8B5E3C"];
const TOOLS = [
  { id: "pen", icon: Pencil, label: "Pen" },
  { id: "line", icon: Minus, label: "Line" },
  { id: "rect", icon: Square, label: "Rect" },
  { id: "circle", icon: Circle, label: "Circle" },
  { id: "eraser", icon: Eraser, label: "Eraser" },
];

export function DrawingCanvas() {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const startPt = useRef(null);
  const snapshot = useRef(null);
  const undoStack = useRef([]);
  const [tool, setTool] = useState("pen");
  const [color, setColor] = useState("#141414");
  const [size, setSize] = useState(4);
  const [fill, setFill] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      const data = canvas.toDataURL();
      canvas.width = rect.width;
      canvas.height = 460;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0);
      img.src = data;
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const pushUndo = () => {
    const ctx = canvasRef.current.getContext("2d");
    undoStack.current.push(ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height));
    if (undoStack.current.length > 25) undoStack.current.shift();
  };

  const drawShape = (ctx, a, b) => {
    ctx.lineWidth = size; ctx.strokeStyle = color; ctx.fillStyle = color; ctx.lineCap = "round";
    if (tool === "line") { ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke(); }
    else if (tool === "rect") {
      const x = Math.min(a.x, b.x), y = Math.min(a.y, b.y), w = Math.abs(b.x - a.x), h = Math.abs(b.y - a.y);
      fill ? ctx.fillRect(x, y, w, h) : ctx.strokeRect(x, y, w, h);
    } else if (tool === "circle") {
      const r = Math.hypot(b.x - a.x, b.y - a.y);
      ctx.beginPath(); ctx.arc(a.x, a.y, r, 0, Math.PI * 2);
      fill ? ctx.fill() : ctx.stroke();
    }
  };

  const start = (e) => {
    drawing.current = true;
    pushUndo();
    const p = pos(e); startPt.current = p;
    const ctx = canvasRef.current.getContext("2d");
    if (tool === "pen" || tool === "eraser") { ctx.beginPath(); ctx.moveTo(p.x, p.y); }
    else snapshot.current = ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height);
  };
  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    if (tool === "pen" || tool === "eraser") {
      ctx.lineWidth = tool === "eraser" ? size * 4 : size;
      ctx.lineCap = "round";
      ctx.strokeStyle = tool === "eraser" ? "#FFFFFF" : color;
      ctx.lineTo(p.x, p.y); ctx.stroke(); ctx.beginPath(); ctx.moveTo(p.x, p.y);
    } else {
      ctx.putImageData(snapshot.current, 0, 0);
      drawShape(ctx, startPt.current, p);
    }
  };
  const end = () => { drawing.current = false; canvasRef.current.getContext("2d").beginPath(); snapshot.current = null; };

  const undo = () => {
    if (!undoStack.current.length) return;
    canvasRef.current.getContext("2d").putImageData(undoStack.current.pop(), 0, 0);
  };
  const clear = () => {
    pushUndo();
    const ctx = canvasRef.current.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
  };
  const save = () => {
    const link = document.createElement("a");
    link.download = "my-dream-home.png";
    link.href = canvasRef.current.toDataURL();
    link.click();
  };

  return (
    <div data-testid="drawing-canvas">
      <div className="flex flex-wrap items-center gap-3 border border-b-0 border-border bg-card p-4">
        {/* tools */}
        <div className="flex items-center gap-1">
          {TOOLS.map((t) => (
            <button key={t.id} onClick={() => setTool(t.id)} data-testid={`tool-${t.id}`} title={t.label}
              className={`flex h-9 w-9 items-center justify-center border transition-colors ${tool === t.id ? "border-cyan bg-cyan text-white" : "border-border text-foreground hover:border-cyan"}`}>
              <t.icon className="h-4 w-4" />
            </button>
          ))}
          <button onClick={() => setFill(!fill)} data-testid="tool-fill" title="Fill shapes"
            className={`flex h-9 w-9 items-center justify-center border transition-colors ${fill ? "border-cyan bg-cyan text-white" : "border-border text-foreground hover:border-cyan"}`}>
            <PaintBucket className="h-4 w-4" />
          </button>
        </div>
        <div className="h-6 w-px bg-border" />
        {/* colors */}
        <div className="flex items-center gap-2">
          {COLORS.map((c) => (
            <button key={c} onClick={() => { setColor(c); if (tool === "eraser") setTool("pen"); }} data-testid={`color-${c}`}
              className="h-7 w-7 border transition-transform hover:scale-110"
              style={{ background: c, borderColor: color === c ? "#141414" : "#cbc8c2", boxShadow: color === c ? "0 0 0 2px #141414" : "none" }} />
          ))}
        </div>
        <div className="h-6 w-px bg-border" />
        <div className="flex items-center gap-2">
          <input type="range" min="2" max="30" value={size} onChange={(e) => setSize(Number(e.target.value))} data-testid="brush-size" className="accent-cyan" />
        </div>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={undo} data-testid="canvas-undo" className="rounded-none border-border font-mono text-xs uppercase hover:border-foreground">
            <Undo2 className="mr-1 h-4 w-4" /> Undo
          </Button>
          <Button size="sm" variant="outline" onClick={clear} data-testid="canvas-clear" className="rounded-none border-border font-mono text-xs uppercase hover:border-destructive hover:text-destructive">
            <Trash2 className="mr-1 h-4 w-4" /> Clear
          </Button>
          <Button size="sm" onClick={save} data-testid="canvas-save" className="rounded-none bg-cyan font-mono text-xs uppercase text-white hover:bg-cyan/90">
            <Download className="mr-1 h-4 w-4" /> Save
          </Button>
        </div>
      </div>
      <div className="border border-border bg-white">
        <canvas
          ref={canvasRef}
          className="block w-full cursor-crosshair touch-none"
          onMouseDown={start} onMouseMove={draw} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={draw} onTouchEnd={end}
        />
      </div>
    </div>
  );
}
